import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogModule, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { MatRadioChange, MatRadioModule } from "@angular/material/radio";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectChange, MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { provideNativeDateAdapter } from "@angular/material/core";
import { CONSTANTS } from "../../../../Constants/Constants";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import {
  CollaborationHistoryEnum,
  CostReviewDatabaseRecord, CostReviewerDetails,
  CostReviewOperation,
  CostReviewStatus, CostReviewOperationResult, ForcePublishDetails, 
  SendReviewOrReplaceReviewerRequest,
  ReviewerTypeItem
} from "../../../../models/dtos";
import { CommonDataService } from "../../../../services/common-data.service";
import { Subject, take, takeUntil } from "rxjs";
import { GetReviewDetailsService } from "../../../../services/api/get-review-details.service";
import { UserProfileService } from "../../../../services/common/user-profile.service";
import { CostReviewRecordBuilderService } from "../../../../services/review/cost-review-record-builder.service";
import { ConfirmationDialogService } from "../../../../services/common/confirmation-dialog.service";
import { NotificationMessageService } from "../../../../services/common/notification-message.service";
import { CommonMetaDataService } from "../../../../services/common-meta-data.service";
import { EstimateSaveService } from "../../../../services/estimate-save.service";
import { ISubmittalPackages } from "../../../../models/properties/ISubmittalPackages";
import { GocCollaborationRestrictionHandlerService } from "../../../../services/common/goc-collaboration-restriction-handler.service";
import { MultiPdfReportDropdownComponent } from '../../../Common/multi-pdf-report-dropdown/multi-pdf-report-dropdown.component';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';
import { CommonFunctionsService } from "../../../../services/common-functions.service";

@Component({
  selector: 'app-cost-cancel-request-dialog',
  templateUrl: './cost-review-cancel.component.html',
  styleUrl: './cost-review-cancel.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatDialogModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    TranslatePipe,
    MultiPdfReportDropdownComponent,
    CloseOnEscapeDirective
  ],
  providers: [provideNativeDateAdapter()]
})
export class CostReviewCancelDialog implements OnInit, OnDestroy {

  minDate: Date = new Date();
  readonly ALL_REVIEWERS_VALUE = '__ALL__';
  public isAllReviewersSelected: boolean = false;
  reportsList: ISubmittalPackages[] = [];
  availableReviewers: CostReviewerDetails[] = [];
  selectedReviewer: CostReviewerDetails | null = null;
  assignedReviewers: CostReviewerDetails[] = [];
  reviewerRoles: ReviewerTypeItem[] = [];
  visibleReviewerRoles: ReviewerTypeItem[] = [];
  modificationNotes: string = '';
  estimateId: string = '';
  costReviewData: CostReviewDatabaseRecord = null;
  isInCancelMode: boolean = false;
  isReviewerVerified: boolean = false;
  verifiedUserId: string = CONSTANTS.EMPTY_STRING;
  private readonly destroy$ = new Subject<void>();
  costReviewCancelForm: FormGroup<{
    reviewer: FormControl<string>;
    available: FormControl<string>;
    role: FormControl<number>;
    name: FormControl<string>;
    email: FormControl<string>;
    requiredByDate: FormControl<Date>;
    emailNote: FormControl<string>;
    requestType: FormControl<string>;
    report1: FormControl<string>;
    report2: FormControl<string>;
    report3: FormControl<string>;
  }> = new FormGroup({
    reviewer: new FormControl<string>(CONSTANTS.EMPTY_STRING),
    available: new FormControl<string>(CONSTANTS.EMPTY_STRING),
    role: new FormControl<number>(1, [Validators.required]),
    name: new FormControl<string>(CONSTANTS.EMPTY_STRING, [Validators.required]),
    email: new FormControl<string>(CONSTANTS.EMPTY_STRING, [Validators.required, Validators.email]),
    requiredByDate: new FormControl<Date | null>(null),
    emailNote: new FormControl<string>(CONSTANTS.EMPTY_STRING),
    requestType: new FormControl<string>(CONSTANTS.ZERO_STRING, [Validators.required]),
    report1: new FormControl<string>({ value: CONSTANTS.EMPTY_STRING, disabled: true }),
    report2: new FormControl<string>({ value: CONSTANTS.EMPTY_STRING, disabled: true }),
    report3: new FormControl<string>({ value: CONSTANTS.EMPTY_STRING, disabled: true })
  });
  constructor(
    private readonly dialogRef: MatDialogRef<CostReviewCancelDialog>,
    private readonly commonDataService: CommonDataService,
    private readonly getReviewDetailsService: GetReviewDetailsService,
    private readonly translate: TranslateService,
    private readonly userProfileService: UserProfileService,
    private readonly costReviewRecordBuilderService: CostReviewRecordBuilderService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly notificationMessageService: NotificationMessageService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly estimateSaveService: EstimateSaveService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly commonFunctionsService: CommonFunctionsService
  ) { }

  trackByAssignedReviewerId(index: number, reviewer: CostReviewerDetails): string {
    return reviewer.reviewUserId;
  }

  trackByReviewerId(index: number, reviewer: CostReviewerDetails): string {
    return reviewer.reviewUserId;
  }

  getReviewerDisplayLabel(reviewer: CostReviewerDetails): string {
    const statusString = this.costReviewRecordBuilderService.mapStatusToTranslationKey(reviewer.reviewerStatus);
    if (reviewer.reviewerStatus === CostReviewStatus.Approved || reviewer.reviewerStatus === CostReviewStatus.Denied) {
      const closeDate = reviewer.closeDate ? new Date(reviewer.closeDate).toLocaleDateString() : '';
      return `${reviewer.reviewerName} [${this.translate.instant(statusString)} ${closeDate}]`;
    } else {
      const requiredBy = reviewer.requiredBy ? new Date(reviewer.requiredBy).toLocaleDateString() : '';
      return `${reviewer.reviewerName} [${this.translate.instant(statusString)} ${requiredBy}]`;
    }
  }

  trackByRoleId(index: number, role: ReviewerTypeItem): number {
    return role.id;
  }

  get isSendDisabled(): boolean {
    return (this.costReviewCancelForm.invalid || this.isAllReviewersSelected)
      || this.isInCancelMode
      || !this.isReviewerVerified;
  }

  get isSelectedReviewerPolicyMandated(): boolean {
    return !!this.selectedAssignedReviewer?.isPolicyMandatedApprover;
  }

  get isCancelDisabled(): boolean {
    return this.isSelectedReviewerPolicyMandated
      && !this.isAllReviewersSelected
      && this.assignedReviewers.length > CONSTANTS.ONE;
  }

  get isAvailableReviewerDisabled(): boolean {
    return this.isSelectedReviewerPolicyMandated || this.isAllReviewersSelected;
  }

  get selectedAssignedReviewerRoleDescription(): string {
    const roleId = this.selectedAssignedReviewer?.reviewerTypeID ?? this.costReviewCancelForm.controls.role.value;
    return this.reviewerRoles.find(role => role.id === roleId)?.description ?? CONSTANTS.EMPTY_STRING;
  }

  private get selectedAssignedReviewer(): CostReviewerDetails | null {
    const reviewerUserId = this.costReviewCancelForm.controls['reviewer'].value;
    if (!reviewerUserId || reviewerUserId === this.ALL_REVIEWERS_VALUE) {
      return null;
    }
    return this.assignedReviewers.find(reviewer => reviewer.reviewUserId === reviewerUserId) ?? null;
  }

  ngOnInit(): void {
    this.estimateId = this.commonDataService.getEstimateId();
    this.costReviewData = this.costReviewRecordBuilderService.costReviewTableData;
    this.reviewerRoles = this.commonMetaDataService.getMetaData()?.costReviewerTypes ?? [];
    this.visibleReviewerRoles = this.reviewerRoles.filter(role => !role.isPolicyMandatedApprover);
    const availableReviewers = this.commonDataService.getPreferenceData().costReviewerPreferences;
    if (availableReviewers?.crAvailableReviewers && availableReviewers.crAvailableReviewers?.length) {
      this.availableReviewers = availableReviewers.crAvailableReviewers;
    }
    this.assignedReviewers = this.costReviewData.costReviewers;
    this.restoreEmailAndReportSelections();
    if (this.assignedReviewers.length === CONSTANTS.ONE) {
      this.costReviewCancelForm.patchValue({ reviewer: this.assignedReviewers[CONSTANTS.ZERO].reviewUserId });
      this.updateReviewerSelectionState(this.assignedReviewers[CONSTANTS.ZERO].reviewUserId);
    } else if (this.assignedReviewers.length > CONSTANTS.ONE) {
      this.costReviewCancelForm.patchValue({ reviewer: this.ALL_REVIEWERS_VALUE });
      this.isAllReviewersSelected = true;
      this.disableFormControls();
    }
    this.costReviewCancelForm.get('email').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isReviewerVerified = false;
    });
    this.updateVisibleReportItems();
  }

   private updateVisibleReportItems(): void {
     this.reportsList = this.costReviewRecordBuilderService.getReportDropdownItems();
  }

  /** Mirrors the email/report choices made when the review was requested. */
  private restoreEmailAndReportSelections(): void {
    this.costReviewCancelForm.patchValue({
      requestType: this.costReviewData.autoSendEmail ? '1' : CONSTANTS.ZERO_STRING,
      report1: this.toReportControlValue(this.costReviewData.reportOne),
      report2: this.toReportControlValue(this.costReviewData.reportTwo),
      report3: this.toReportControlValue(this.costReviewData.reportThree)
    });
    this.toggleReportControls(this.costReviewCancelForm.controls.requestType.value);
  }

  private toReportControlValue(reportId: string): string {
    return !reportId || reportId === CONSTANTS.NEGATIVE_ONE_STRING ? CONSTANTS.EMPTY_STRING : reportId;
  }

  onReviewerChange(event: MatSelectChange): void {
    this.updateReviewerSelectionState(event.value);
  }

  private updateReviewerSelectionState(selectedReviewerId: string): void {
    if (selectedReviewerId === this.ALL_REVIEWERS_VALUE) {
      this.costReviewCancelForm.patchValue({
        name: CONSTANTS.EMPTY_STRING,
        email: CONSTANTS.EMPTY_STRING
      });
      this.isAllReviewersSelected = true;
      this.disableFormControls();
      return;
    }
    this.isAllReviewersSelected = false;
    this.enableFormControls();

    const selectedReviewer = this.assignedReviewers.find(reviewer => reviewer.reviewUserId === selectedReviewerId);
    if (selectedReviewer?.isPolicyMandatedApprover) {
      this.costReviewCancelForm.patchValue({
        role: selectedReviewer.reviewerTypeID,
      });
      this.costReviewCancelForm.controls.available.disable();
      this.costReviewCancelForm.controls.role.disable();
    } else {
      const firstVisibleRoleId = this.visibleReviewerRoles[CONSTANTS.ZERO]?.id;
      if (firstVisibleRoleId === undefined) {
        return;
      }
      this.costReviewCancelForm.patchValue({
        role: firstVisibleRoleId,
      });
    }
  }

  private disableFormControls(): void {
    const controls = this.costReviewCancelForm.controls;
    controls.available.disable();
    controls.role.disable();
    controls.name.disable();
    controls.email.disable();
    controls.requiredByDate.disable();
    controls.emailNote.disable();
    controls.requestType.disable();
    controls.report1.disable();
    controls.report2.disable();
    controls.report3.disable();
  }

  private enableFormControls(): void {
    const controls = this.costReviewCancelForm.controls;
    controls.available.enable();
    controls.role.enable();
    controls.name.enable();
    controls.email.enable();
    controls.requiredByDate.enable();
    controls.emailNote.enable();
    controls.requestType.enable();
    this.toggleReportControls(controls.requestType.value);
  }

  onRequestTypeChange(event: MatRadioChange): void {
    this.toggleReportControls(event.value);
  }

  private toggleReportControls(requestType: string): void {
    const controls = this.costReviewCancelForm.controls;
    if (requestType === '1') {
      controls.report1.enable();
      controls.report2.enable();
      controls.report3.enable();
    } else {
      controls.report1.disable();
      controls.report2.disable();
      controls.report3.disable();
    }
  }

  generateCostReviewersList(): CostReviewerDetails[] {
    const selectedRole = this.reviewerRoles.find(role => role.id === this.costReviewCancelForm.controls['role'].value);
    const selectedUser = this.assignedReviewers.find(reviewer => reviewer.reviewUserId === this.costReviewCancelForm.controls['reviewer'].value);
    selectedUser.reviewerTypeID = selectedRole?.id ?? 0;
    selectedUser.reviewerName = this.costReviewCancelForm.controls['name'].value;
    selectedUser.reviewerEmail = this.costReviewCancelForm.controls['email'].value;
    const selectedDate = this.costReviewCancelForm.controls['requiredByDate'].value;
    const requiredByDate = selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 12) : null;
    selectedUser.requiredBy = requiredByDate;
    selectedUser.reviewUserId = this.verifiedUserId ? this.verifiedUserId : this.selectedReviewer?.reviewUserId ?? CONSTANTS.EMPTY_STRING;
    selectedUser.originalReviewerId = this.verifiedUserId ? this.verifiedUserId : this.selectedReviewer?.reviewUserId ?? CONSTANTS.EMPTY_STRING;
    selectedUser.modifiedBy = this.commonDataService.getUserId();
    selectedUser.closeDate = selectedUser.closeDate ?? null;
    //selectedUser.isPolicyMandatedApprover = selectedUser?.isPolicyMandatedApprover ?? false;
    return [...this.assignedReviewers];
  }

  getForcePublishDetailsForReplace(currentReviewer: CostReviewerDetails | null): ForcePublishDetails | null {
    if(!this.commonDataService.isCollaborationEstimate()) {
      return null;
    }
    const oldReviewer = currentReviewer;
    const newReviewerName = this.costReviewCancelForm.controls.name.value ?? CONSTANTS.EMPTY_STRING;
    const newReviewerEmail = this.costReviewCancelForm.controls.email.value ?? CONSTANTS.EMPTY_STRING;

    const forcePublishAdditionalInfo = this.formatString(
      CONSTANTS.ESTIMATE_FORCE_PUBLISH_COST_REVIEWER_REPLACE,
      oldReviewer?.reviewerName ?? CONSTANTS.EMPTY_STRING,
      oldReviewer?.reviewerEmail ?? CONSTANTS.EMPTY_STRING,
      newReviewerName,
      newReviewerEmail
    );
    this.modificationNotes = this.translate.instant('MODIFICATION_NOTES_COST_REVIEWER_REPLACED', { 
      oldReviewerName:  oldReviewer?.reviewerName ?? CONSTANTS.EMPTY_STRING,
      oldReviewerEmail: oldReviewer?.reviewerEmail ?? CONSTANTS.EMPTY_STRING,
      newReviewerName: newReviewerName,
      newReviewerEmail: newReviewerEmail
    });

    const additionalInfo = this.formatString(
      CONSTANTS.ESTIMATE_ADD_ON_COST_REVIEWER_REPLACE,
      oldReviewer?.reviewerName ?? CONSTANTS.EMPTY_STRING,
      oldReviewer?.reviewerEmail ?? CONSTANTS.EMPTY_STRING,
      newReviewerName,
      newReviewerEmail
    );
    return {
      forcePublishAdditionalInfo: forcePublishAdditionalInfo,
      operation: CollaborationHistoryEnum.CostReviewerReplaced,
      modificationNotes: this.modificationNotes,
      estimateCheckoutAdditionalInfo: additionalInfo,
      isEstimateBeingCheckedOutAfterPublish: true
    }
  }

  private formatString(template: string, ...args: string[]) {
        return template.replace(/\{(\d+)\}/g, (_, index) => 
            args[Number(index)] ?? CONSTANTS.EMPTY_STRING
        );
  }

  generateCostReviewData(status: number = CostReviewStatus.InProcess) {
    this.costReviewData.requesterNotes = this.costReviewCancelForm.controls['emailNote'].value || CONSTANTS.EMPTY_STRING;
    this.costReviewData.autoSendEmail = this.costReviewCancelForm.controls['requestType'].value === '1';
    this.costReviewData.reportOne = String(this.costReviewCancelForm.controls['report1'].value || CONSTANTS.NEGATIVE_ONE_STRING);
    this.costReviewData.reportTwo = String(this.costReviewCancelForm.controls['report2'].value || CONSTANTS.NEGATIVE_ONE_STRING);
    this.costReviewData.reportThree = String(this.costReviewCancelForm.controls['report3'].value || CONSTANTS.NEGATIVE_ONE_STRING);
    this.costReviewData.costReviewers = this.generateCostReviewersList();
    this.costReviewData.ccUser = this.costReviewRecordBuilderService.generateCCUserList();
    this.costReviewData.status = status;
    this.costReviewData.isReviewerReplaced = true;
    this.costReviewData.reviewObject = this.costReviewRecordBuilderService.getBusinessSpecificReviewObject();
    this.costReviewData.newReviewObject = this.costReviewRecordBuilderService.generateNewCostReviewAttributeRequestData();
  }

  async generateSendRequestData(): Promise<SendReviewOrReplaceReviewerRequest> {
    const currentReviewer = this.selectedAssignedReviewer ? { ...this.selectedAssignedReviewer } : null;
    this.generateCostReviewData();
    const saveRequest = await this.estimateSaveService.createSaveEstimateRequest();
    const requestData: SendReviewOrReplaceReviewerRequest = {
      requestorName: this.userProfileService.userInfo?.display_name || CONSTANTS.EMPTY_STRING,
      cultureId: this.commonDataService.getEstimateCultureId() || CONSTANTS.ONE,
      operation: CostReviewOperation.ReplaceReviewer,
      saveRequest,
      reports: await this.costReviewRecordBuilderService.getSelectedReportsPdf(
        this.costReviewRecordBuilderService.getSelectedReports(this.costReviewCancelForm)
      ),
      forcePublishDetails: this.getForcePublishDetailsForReplace(currentReviewer)
    };
    return requestData;
  }


  async onSend(): Promise<void> {
    const emailId = this.costReviewCancelForm.value.email;
    const alreadyAssigned = this.assignedReviewers.some(
      r => r.reviewerEmail?.toLowerCase() === emailId?.toLowerCase()
    );
    if (alreadyAssigned) {
      this.notificationMessageService.showNotificationMessageDialog(
        CONSTANTS.TYPE_ERROR,
        'ERROR_TITLE',
        'COST_REVIEW_REVIEWER_ALREADY_ASSIGNED_ERROR',
        CONSTANTS.DEFAULT_DIALOG_WIDTH
      );
      return;
    }
    const requestData = await this.generateSendRequestData();
    const isManualEmail = this.costReviewCancelForm.value.requestType === CONSTANTS.ZERO_STRING;

    this.getReviewDetailsService.sendReviewOrReplaceReviewer(requestData)
      .pipe(take(1)).subscribe({
        next: (response) => {
          if (response?.result === CostReviewOperationResult.Success) {
            if (isManualEmail) {
              const recipientEmails = [this.costReviewCancelForm.value.email];
              this.costReviewRecordBuilderService.openMailClient(response.emailSubject, response.emailBody, recipientEmails);
            }
            this.dialogRef.close({
              isRequested: true,
              isCancelled: false,
              isReviewerReplaced: true,
              updatedCostReviewers: this.assignedReviewers,
              costReviewData: this.costReviewData
            });
            if (this.commonDataService.isCollaborationEstimate() && response.collaborationHubData) {
              const collaborationHubData = response.collaborationHubData;
              this.commonFunctionsService.updateCollaborationHubData(collaborationHubData);
            }
          }
        },
        error: async (error) => {
          if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
            return;
          }
          console.error('API failed — dialog stays open, no state change');
        }
      });
  }

  get formControls() {
    return this.costReviewCancelForm.controls;
  }

  onAvailableReviewerChange(event: MatSelectChange): void {
    const selectedUserId = event.value;
    this.selectedReviewer = this.availableReviewers.find(reviewer => reviewer.reviewUserId === selectedUserId);

    if (this.selectedReviewer) {
      this.costReviewCancelForm.patchValue({
        name: this.selectedReviewer.reviewerName || CONSTANTS.EMPTY_STRING,
        email: this.selectedReviewer.reviewerEmail || CONSTANTS.EMPTY_STRING
      });
      this.isReviewerVerified = true;
    }
  }

  onVerifyClick(): void {
    const emailId = this.costReviewCancelForm.controls['email'].value;
    if (!emailId) {
      return;
    }
    this.isReviewerVerified = false;
    this.getReviewDetailsService.verifyCostReviewer({ emailId })
      .pipe(take(1))
      .subscribe(response => {
        if (response.isValidReviewer) {
          this.isReviewerVerified = true;
          this.verifiedUserId = response.bsetUserId;
        } else {
          this.isReviewerVerified = false;
          this.verifiedUserId = CONSTANTS.EMPTY_STRING;
          this.notificationMessageService.showNotificationMessageDialog(
            CONSTANTS.TYPE_ERROR,
            'ERROR_TITLE',
            'COST_REVIEW_ADD_MODIFY_UNAUTHORIZED_REVIEWER_ERROR',
            CONSTANTS.DEFAULT_DIALOG_WIDTH
          );
        }
        // null selected id
        this.selectedReviewer = null;
      });
  }

  onCancel() {
    this.confirmationDialogService.confirmWarning('COST_REVIEW_CANCEL_CONFIRMATION_MESSAGE')
      .pipe(take(1))
      .subscribe(confirmed => {
        if (confirmed) {
          this.executeCancelReview();
        }
      });
  }

  private executeCancelReview() {
    this.isInCancelMode = true;
    const selectedReviewerId = this.costReviewCancelForm.controls['reviewer'].value;
    const isAllSelected = selectedReviewerId === this.ALL_REVIEWERS_VALUE;
    const costReviewers: CostReviewerDetails[] = isAllSelected
      ? []
      : this.assignedReviewers.filter(r => r.reviewUserId !== selectedReviewerId);
    this.costReviewData.requesterNotes = this.costReviewCancelForm.controls['emailNote'].value || CONSTANTS.EMPTY_STRING;
    this.costReviewData.autoSendEmail = !isAllSelected && this.costReviewCancelForm.controls['requestType'].value === '1';
    this.costReviewData.reportOne = this.resolveCancelReportValue(isAllSelected, 'report1');
    this.costReviewData.reportTwo = this.resolveCancelReportValue(isAllSelected, 'report2');
    this.costReviewData.reportThree = this.resolveCancelReportValue(isAllSelected, 'report3');
    this.costReviewData.costReviewers = costReviewers;
    this.costReviewData.ccUser = this.costReviewRecordBuilderService.generateCCUserList();
    this.costReviewData.status = isAllSelected ? CostReviewStatus.NotStarted : CostReviewStatus.InProcess;
    const estimateData = this.commonDataService.getEstimateData();

    const requestData = this.costReviewRecordBuilderService.buildCancelOrUnlockRequest(this.costReviewData, this.getForcePublishDetailsForCancel(this.selectedAssignedReviewer), estimateData);
    this.getReviewDetailsService.cancelOrUnlockCostReview(requestData, true).pipe(take(1)).subscribe({
      next: (response) => {
        this.isInCancelMode = false;
        if (response?.result === CostReviewOperationResult.Success) {
          // The request had to carry the remaining reviewers so the backend derives the right status; keep the previous ones for a future request.
          if (costReviewers.length === CONSTANTS.ZERO) {
            this.costReviewData.reviewObject.reviewerDetailsForRequestPreference = this.costReviewRecordBuilderService.retainReviewersForNextRequest(this.assignedReviewers);
          }       
          this.dialogRef.close({ 
            isRequested: true,
            isCancelled: true,
            isReviewerReplaced: false,
            updatedCostReviewers: costReviewers,
            costReviewData: this.costReviewData
          });
          if (this.commonDataService.isCollaborationEstimate() && response.collaborationHubData) {
            const collaborationHubData = response.collaborationHubData;
            this.commonFunctionsService.updateCollaborationHubData(collaborationHubData);
          }
        }
      },
      error: async (error) => {
        this.isInCancelMode = false;
        if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
          return;
        }
      }
    });
  }

  private getForcePublishDetailsForCancel(currentReviewer: CostReviewerDetails): ForcePublishDetails | null {
    if (!this.commonDataService.isCollaborationEstimate()) {
      return null;
    }
    const reviewerName = currentReviewer?.reviewerName ?? CONSTANTS.EMPTY_STRING;
    const reviewerEmail = currentReviewer?.reviewerEmail ?? CONSTANTS.EMPTY_STRING;    
        
    if(this.isAllReviewersSelected || this.assignedReviewers.length === CONSTANTS.ONE) {
      const currentUserEmail: string = this.userProfileService.userInfo?.mail ?? CONSTANTS.EMPTY_STRING;
      const currentCollaboratorData = this.commonDataService.getCollaboratorData().find(collaborator => collaborator.email === currentUserEmail);
      return {
        forcePublishAdditionalInfo: CONSTANTS.ESTIMATE_FORCE_PUBLISH_COST_REVIEW_CANCEL,
        operation: CollaborationHistoryEnum.CostReviewCancelled,
        modificationNotes: this.translate.instant('MODIFICATION_NOTES_COST_REVIEW_CANCELLED', { name: currentCollaboratorData.name }),
        estimateCheckoutAdditionalInfo: CONSTANTS.ESTIMATE_ADD_ON_COST_REVIEW_CANCEL,
        isEstimateBeingCheckedOutAfterPublish: true
      };
    }
    
    const forcePublishAdditionalInfo = this.formatString(
      CONSTANTS.ESTIMATE_FORCE_PUBLISH_COST_REVIEWER_REMOVE, reviewerName ?? CONSTANTS.EMPTY_STRING, reviewerEmail ?? CONSTANTS.EMPTY_STRING
    )
    const additionalInfo =  this.formatString(
      CONSTANTS.ESTIMATE_ADD_ON_COST_REVIEW_REMOVE, reviewerName ?? CONSTANTS.EMPTY_STRING, reviewerEmail ?? CONSTANTS.EMPTY_STRING
    )
    return {
      forcePublishAdditionalInfo: forcePublishAdditionalInfo,
      operation: CollaborationHistoryEnum.CostReviewerRemoved,
      modificationNotes: this.translate.instant('MODIFICATION_NOTES_COST_REVIEWER_REMOVED', { name: reviewerName , email: reviewerEmail }),
      estimateCheckoutAdditionalInfo: additionalInfo,
      isEstimateBeingCheckedOutAfterPublish: true
    };
    }

  /** Cancelling every reviewer ends the review, so the report selections no longer apply. */
  private resolveCancelReportValue(isAllSelected: boolean, controlName: 'report1' | 'report2' | 'report3'): string {
    if (isAllSelected) {
      return CONSTANTS.NEGATIVE_ONE_STRING;
    }
    return String(this.costReviewCancelForm.controls[controlName].value || CONSTANTS.NEGATIVE_ONE_STRING);
  }

  get isVerifyDisabled(){
    return !this.costReviewCancelForm.controls.email.value || this.isReviewerVerified || this.isAllReviewersSelected;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.verifiedUserId = CONSTANTS.EMPTY_STRING;
    this.estimateId = CONSTANTS.EMPTY_STRING;
    this.costReviewCancelForm.reset();
    this.assignedReviewers = [];
    this.costReviewData = null;
    this.isAllReviewersSelected = false;
    this.selectedReviewer = null;
    this.isReviewerVerified = false;
  }

}
